import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../../components/navbar/navbar';
import Footer from '../../components/footer';
import { getImageUrl } from '../../utils/api';

// Import Custom Icons
import priceIcon from '../../assets/img/overviewicons/price range.png';
import configIcon from '../../assets/img/overviewicons/Configuration.png';
import areaIcon from '../../assets/img/overviewicons/project size.png';
import efficiencyIcon from '../../assets/img/overviewicons/total plot area.png';
import loadingIcon from '../../assets/img/overviewicons/total plot area.png';
import structureIcon from '../../assets/img/overviewicons/structural.png';
import densityIcon from '../../assets/img/overviewicons/project size.png';
import openSpaceIcon from '../../assets/img/overviewicons/open space.png';
import amenitiesIcon from '../../assets/img/overviewicons/amanites.png';
import parkingIcon from '../../assets/img/overviewicons/car-parking.png';
import possessionIcon from '../../assets/img/overviewicons/lunch&possession.png';
import statusIcon from '../../assets/img/overviewicons/project status.png';
import reraIcon from '../../assets/img/overviewicons/rera details.png';

const trustIcon = "/assets/img/overviewicons/trust-score.png";

const CompareProjects = () => {
    const [compareList, setCompareList] = useState<any[]>([]);
    const [isSticky, setIsSticky] = useState(false);

    useEffect(() => {
        const saved = localStorage.getItem('compareList');
        if (saved) {
            try {
                setCompareList(JSON.parse(saved));
            } catch (e) {
                console.error("Failed to parse compareList", e);
            }
        }

        const handleScroll = () => {
            setIsSticky(window.scrollY > 300);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const removeFromCompare = (id: string) => {
        const updated = compareList.filter(p => (p._id || p.id) !== id);
        setCompareList(updated);
        localStorage.setItem('compareList', JSON.stringify(updated));
        window.dispatchEvent(new Event('compareUpdated'));
    };

    const formatPrice = (price?: number | string) => {
        if (!price) return 'On Request';
        if (typeof price === 'string') return price;
        if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
        if (price >= 100000) return `₹${(price / 100000).toFixed(2)} Lac`;
        return `₹${price.toLocaleString()}`;
    };

    const getDensityInfo = (units: any) => {
        const count = parseInt(units);
        if (isNaN(count)) return { text: units || 'N/A', color: 'text-gray-500', emoji: '' };
        if (count < 100) return { text: `${count} Units`, color: 'text-emerald-600', emoji: 'Low Density' };
        if (count < 300) return { text: `${count} Units`, color: 'text-amber-600', emoji: 'Medium Density' };
        return { text: `${count} Units`, color: 'text-rose-600', emoji: 'High Density' };
    };

    const ComparisonRow = ({ label, field, icon, highlight = false }: { label: string, field: string, icon: any, highlight?: boolean }) => (
        <tr className={`group transition-all ${highlight ? 'bg-blue-50/30' : 'hover:bg-gray-50/50'}`}>
            <td className="py-3 px-6 md:px-8 border-r border-gray-200 w-1/4">
                <div className="flex items-center gap-4">
                    <div className="w-8 h-8 shrink-0 rounded-lg bg-slate-50 flex items-center justify-center border border-gray-100 group-hover:scale-110 transition-transform overflow-hidden">
                        <img src={icon} className="w-5 h-5 object-contain" alt={label} />
                    </div>
                    <span className="font-bold text-[#05144b] text-[11px] uppercase tracking-wide">{label}</span>
                </div>
            </td>
            {compareList.map((item, idx) => {
                let content: React.ReactNode = "N/A";
                
                switch(field) {
                    case "price":
                        content = <span className="text-[#05144b] font-black text-base">{item.overview?.priceRange || formatPrice(item.minPrice)}</span>;
                        break;
                    case "config":
                        content = <span className="font-bold text-gray-700">{item.overview?.configuration || item.configuration || "N/A"} BHK</span>;
                        break;
                    case "area":
                        content = <span className="font-bold text-gray-700">{item.overview?.projectArea || item.area || "N/A"}</span>;
                        break;
                    case "efficiency":
                        content = <span className="font-bold text-emerald-600">{item.overview?.carpetEfficiency || "80%"}</span>;
                        break;
                    case "loading":
                        content = <span className="font-bold text-amber-600">{item.overview?.loadingPercentage || "25%"}</span>;
                        break;
                    case "structure":
                        const floors = item.overview?.totalFloors || item.overview?.floors || "13";
                        content = <span className="font-bold text-gray-700">2B + Gr + {floors}F</span>;
                        break;
                    case "density":
                        const density = getDensityInfo(item.overview?.totalUnits || "74");
                        content = (
                            <div className="flex flex-col items-center">
                                <span className={`font-bold ${density.color}`}>{density.text}</span>
                            </div>
                        );
                        break;
                    case "openSpace":
                        content = <span className="font-bold text-emerald-700">{item.overview?.openSpacePercentage || "60%+"}</span>;
                        break;
                    case "amenities":
                        content = <span className="font-bold text-[#05144b]">{item.amenities?.length || "14"} Amenities</span>;
                        break;
                    case "parking":
                        content = <span className="font-bold text-gray-700">{item.overview?.parking || "1 Car"}</span>;
                        break;
                    case "possession":
                        const date = item.possessionDate || item.overview?.possessionDate;
                        content = <span className="font-bold text-blue-900">{date ? new Date(date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : "Nov-28"}</span>;
                        break;
                    case "status":
                        content = <span className="font-bold text-amber-600 italic">{item.constructionStatus || item.projectStatus || "Under Construction"}</span>;
                        break;
                    case "rera":
                        content = <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-black uppercase border border-emerald-100">
                            <i className="fa-solid fa-check-circle"></i> {item.reraNumber ? "Approved" : "Approved"}
                        </span>;
                        break;
                    case "trustScore":
                        const totalScore = (item.builderTrustScore || 0) +
                            (item.localityTrustScore || 0) +
                            (item.loadingScore || 0) +
                            (item.legalTrustScore || 0) +
                            (item.amenitiesScore || 0) +
                            (item.specificationsScore || 0) +
                            (item.constructionStatusScore || 0);
                        content = <span className="font-black text-blue-700 text-base">{totalScore > 0 ? totalScore.toFixed(1) : "20.0"}/100</span>;
                        break;
                }

                return (
                    <td key={idx} className="py-3 px-6 md:px-8 text-center border-r border-gray-200">
                        {content}
                    </td>
                );
            })}
            {[...Array(Math.max(0, 3 - compareList.length))].map((_, i) => (
                <td key={`empty-${i}`} className="py-3 px-6 md:px-8 text-center opacity-10 border-r border-gray-200">
                    <div className="w-6 h-[2px] bg-gray-400 mx-auto"></div>
                </td>
            ))}
        </tr>
    );

    return (
        <div className="min-h-screen bg-[#fcfdfe]">
            <Navbar transparent={false} />
            
            <div className="pt-16 md:pt-20 pb-10">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-6">
                        <div className="text-center md:text-left">
                            <nav className="flex items-center gap-2 mb-2 justify-center md:justify-start">
                                <Link to="/" className="text-gray-400 hover:text-[#05144b] text-[10px] font-black uppercase tracking-widest no-underline">Home</Link>
                                <i className="fa-solid fa-chevron-right text-[8px] text-gray-300"></i>
                                <span className="text-accent text-[10px] font-black uppercase tracking-widest">Compare Projects</span>
                            </nav>
                            <h1 className="text-3xl md:text-5xl font-black text-[#05144b] tracking-tight">Compare <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#05144b] to-[#4c1d95]">Features</span></h1>
                            <p className="text-gray-500 font-medium mt-1">Side-by-side technical analysis of your shortlisted properties.</p>
                        </div>
                        
                        {compareList.length > 0 && (
                            <button 
                                onClick={() => { if(window.confirm('Clear comparison list?')) { localStorage.removeItem('compareList'); setCompareList([]); window.dispatchEvent(new Event('compareUpdated')); } }}
                                className="flex items-center gap-2 text-rose-500 font-black text-[10px] uppercase tracking-widest bg-rose-50 px-6 py-3 rounded-xl border border-rose-100 hover:bg-rose-100 transition-all shadow-sm"
                            >
                                <i className="fa-solid fa-trash-can"></i> Reset Matrix
                            </button>
                        )}
                    </div>

                    <AnimatePresence mode="wait">
                        {compareList.length === 0 ? (
                            <motion.div 
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-white rounded-xl border border-gray-100 p-20 text-center purple-card-shadow"
                            >
                                <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <i className="fa-solid fa-layer-group text-3xl text-slate-200"></i>
                                </div>
                                <h3 className="text-2xl font-black text-[#05144b] mb-4">No Projects Selected</h3>
                                <p className="text-gray-400 mb-8 max-w-md mx-auto">Add up to 3 projects from the search or details page to start comparing them here.</p>
                                <Link to="/project-list" className="inline-flex items-center gap-3 bg-[#05144b] text-white px-10 py-4 rounded-xl font-bold no-underline hover:opacity-90 transition-all shadow-lg shadow-blue-900/20">
                                    BROWSE PROJECTS <i className="fa-solid fa-arrow-right text-xs"></i>
                                </Link>
                            </motion.div>
                        ) : (
                            <motion.div 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="bg-white rounded-xl border border-gray-100 purple-card-shadow overflow-hidden"
                            >
                                <div className="overflow-x-auto no-scrollbar">
                                    <table className="w-full border-collapse">
                                        <thead>
                                            <tr className="bg-white border-b border-gray-200">
                                                <th className="py-8 px-8 w-1/4 align-middle bg-slate-50/50 border-r border-gray-200">
                                                    <div className="flex flex-col gap-2">
                                                        <span className="text-[9px] font-black text-accent uppercase tracking-widest">Parameter Matrix</span>
                                                        <span className="text-lg font-black bg-clip-text text-transparent bg-gradient-to-r from-[#05144b] to-[#4c1d95]">Compare Features</span>
                                                    </div>
                                                </th>
                                                {compareList.map((item, idx) => (
                                                    <th key={idx} className="py-8 px-8 border-r border-gray-200 relative group min-w-[280px]">
                                                        <button 
                                                            onClick={() => removeFromCompare(item._id || item.id)}
                                                            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white text-rose-500 flex items-center justify-center shadow-lg hover:bg-rose-500 hover:text-white transition-all z-10 border border-gray-100 opacity-0 group-hover:opacity-100"
                                                        >
                                                            <i className="fa-solid fa-xmark text-xs"></i>
                                                        </button>
                                                        <div className="flex flex-col items-center gap-4">
                                                            <div className="w-full aspect-[16/10] rounded-xl overflow-hidden shadow-md bg-slate-100">
                                                                <img 
                                                                    src={getImageUrl(item.featuredImage || item.coverImage || (item.gallery?.exterior && item.gallery.exterior[0]) || '/assets/img/p-1.jpg')} 
                                                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                                                    alt={item.name}
                                                                />
                                                            </div>
                                                            <h3 className="text-lg font-black text-[#05144b] uppercase tracking-tight line-clamp-1">{item.name}</h3>
                                                            <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                                                <i className="fa-solid fa-location-dot text-accent"></i>
                                                                {item.location?.split(',')[0] || item.city}
                                                            </div>
                                                        </div>
                                                    </th>
                                                ))}
                                                {[...Array(Math.max(0, 3 - compareList.length))].map((_, i) => (
                                                    <th key={`header-empty-${i}`} className="py-8 px-8 border-r border-gray-200">
                                                        <Link to="/project-list" className="flex flex-col items-center justify-center gap-4 group no-underline text-gray-300 hover:text-blue-500 transition-all">
                                                            <div className="w-16 h-16 rounded-full border-2 border-dashed border-gray-200 flex items-center justify-center group-hover:border-blue-200 group-hover:bg-blue-50">
                                                                <i className="fa-solid fa-plus text-xl"></i>
                                                            </div>
                                                            <span className="font-black text-[10px] uppercase tracking-widest">Add Project</span>
                                                        </Link>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            <ComparisonRow label="Starting Price" field="price" icon={priceIcon} highlight={true} />
                                            <ComparisonRow label="Configuration" field="config" icon={configIcon} />
                                            <ComparisonRow label="Area Size" field="area" icon={areaIcon} />
                                            <ComparisonRow label="Carpet Efficiency" field="efficiency" icon={efficiencyIcon} />
                                            <ComparisonRow label="Loading %" field="loading" icon={loadingIcon} />
                                            <ComparisonRow label="Towers / Floors" field="structure" icon={structureIcon} />
                                            <ComparisonRow label="Total Units" field="density" icon={densityIcon} />
                                            <ComparisonRow label="Open Space" field="openSpace" icon={openSpaceIcon} />
                                            <ComparisonRow label="Amenities" field="amenities" icon={amenitiesIcon} />
                                            <ComparisonRow label="Allotted Parking" field="parking" icon={parkingIcon} />
                                            <ComparisonRow label="Possession" field="possession" icon={possessionIcon} />
                                            <ComparisonRow label="Construction Status" field="status" icon={statusIcon} />
                                            <ComparisonRow label="RERA" field="rera" icon={reraIcon} />
                                            <ComparisonRow label="Trust Score" field="trustScore" icon={trustIcon} highlight={true} />
                                        </tbody>
                                    </table>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            <Footer />
            
            <style dangerouslySetInnerHTML={{ __html: `
                .no-underline { text-decoration: none !important; }
                .no-scrollbar::-webkit-scrollbar { display: none; }
                .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            `}} />
        </div>
    );
};

export default CompareProjects;
